// Mediterranean Language Switcher Component
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  getAvailableLanguages, 
  getCurrentLanguage, 
  changeLanguage,
  getLanguageConfig 
} from '../../services/i18n';
import type { SupportedLocale } from '../../types';
import { Material3Button } from '../ui/Material3Button';

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'flags' | 'compact' | 'expanded';
  className?: string;
  showFlags?: boolean;
  showNativeNames?: boolean;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'dropdown',
  className = '',
  showFlags = true,
  showNativeNames = true,
}) => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  
  const currentLanguage = getCurrentLanguage();
  const currentConfig = getLanguageConfig(currentLanguage);
  const availableLanguages = getAvailableLanguages();
  const getLanguageName = (key?: string) => key ? t(key) : '';
  const getRegionName = (key?: string) => key ? t(key) : '';

  const handleLanguageChange = async (langCode: SupportedLocale) => {
    if (langCode === currentLanguage) {
      setIsOpen(false);
      return;
    }

    setIsChanging(true);
    try {
      await changeLanguage(langCode);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
      // Could show an error toast here
    } finally {
      setIsChanging(false);
    }
  };

  // Compact flag-only variant for mobile navigation
  if (variant === 'flags') {
    return (
      <div className={`flex space-x-2 ${className}`}>
        {availableLanguages.map((lang) => (
          <Material3Button
            key={lang.code}
            variant={currentLanguage === lang.code ? 'filled' : 'outlined'}
            size="md"
            onClick={() => handleLanguageChange(lang.code)}
            disabled={isChanging}
            className={`!text-2xl !min-w-12 !h-12 !p-2 ${isChanging ? 'opacity-50' : ''}`}
            aria-label={t('language.switchTo', { language: getLanguageName(lang.name) })}
          >
            {lang.flag}
          </Material3Button>
        ))}
      </div>
    );
  }

  // Compact current language display
  if (variant === 'compact') {
    return (
      <div className={`relative ${className}`}>
        <Material3Button
          variant="outlined"
          size="md"
          onClick={() => setIsOpen(!isOpen)}
          disabled={isChanging}
          fullWidth
          className={`${isChanging ? 'opacity-50' : ''}`}
          aria-label={t('language.change')}
        >
          <div className="flex items-center space-x-2">
            {showFlags && currentConfig && (
              <span className="text-lg">{currentConfig.flag}</span>
            )}
            <span className="font-medium text-sm uppercase">
              {currentLanguage.split('-')[0]}
            </span>
          </div>
        </Material3Button>
        
        {/* Dropdown */}
        {isOpen && (
          <>
            {/* Backdrop to close dropdown */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown Menu */}
            <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-mediterranean-lg border border-stone-200 py-2 z-50 min-w-[200px]">
              {availableLanguages.map((lang) => (
                <Material3Button
                  key={lang.code}
                  variant="text"
                  size="md"
                  onClick={() => handleLanguageChange(lang.code)}
                  fullWidth
                  className={`!justify-start !px-4 !py-3 !rounded-none hover:!bg-stone-50
                    ${currentLanguage === lang.code ? 'bg-mediterranean-blue-lighter text-mediterranean-blue' : 'text-stone-700'}
                  `}
                >
                  {showFlags && <span className="text-lg">{lang.flag}</span>}
                  <div className="flex-1">
                    <div className="font-medium">{getLanguageName(lang.name)}</div>
                    {showNativeNames && getLanguageName(lang.name) !== lang.nativeName && (
                      <div className="text-sm text-stone-500">{lang.nativeName}</div>
                    )}
                  </div>
                  {currentLanguage === lang.code && (
                    <div className="w-2 h-2 bg-mediterranean-blue rounded-full" />
                  )}
                </Material3Button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  // Default dropdown variant
  return (
    <div className={`relative ${className}`}>
      <Material3Button
        variant="outlined"
        size="md"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isChanging}
        fullWidth
        rightIcon={
          <svg 
            className={`w-5 h-5 text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        }
        className={`!justify-between !px-4 !py-3 ${isChanging ? 'opacity-50' : ''}`}
        aria-label={t('language.select')}
      >
        <div className="flex items-center space-x-3">
          {showFlags && currentConfig && (
            <span className="text-xl">{currentConfig.flag}</span>
          )}
          <div className="text-left">
            <div className="font-medium text-stone-900">{getLanguageName(currentConfig?.name)}</div>
            {showNativeNames && currentConfig && (
              <div className="text-sm text-stone-500">{currentConfig.nativeName}</div>
            )}
          </div>
        </div>
      </Material3Button>

      {/* Dropdown menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-mediterranean-lg border border-stone-200 py-2 z-50">
            {availableLanguages.map((lang) => (
              <Material3Button
                key={lang.code}
                variant="text"
                size="md"
                onClick={() => handleLanguageChange(lang.code)}
                disabled={isChanging}
                fullWidth
                className={`!justify-start !px-4 !py-3 !rounded-none hover:!bg-stone-50
                  ${currentLanguage === lang.code ? '!bg-mediterranean-blue-lighter !text-mediterranean-blue' : '!text-stone-700'}
                  ${isChanging ? 'opacity-50' : ''}
                `}
              >
                <div className="flex items-center space-x-3 w-full">
                  {showFlags && <span className="text-xl">{lang.flag}</span>}
                  <div className="flex-1">
                    <div className="font-medium">{getLanguageName(lang.name)}</div>
                    {showNativeNames && (
                      <div className="text-sm text-stone-500">{lang.nativeName}</div>
                    )}
                    <div className="text-xs text-stone-400">{getRegionName(lang.region)}</div>
                  </div>
                  {currentLanguage === lang.code && (
                    <div className="w-2 h-2 bg-mediterranean-blue rounded-full" />
                  )}
                </div>
              </Material3Button>
            ))}
          </div>
        </>
      )}
      
      {/* Loading indicator */}
      {isChanging && (
        <div className="absolute inset-0 bg-white/75 rounded-lg flex items-center justify-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-mediterranean-blue" />
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
